import React, { useState, useCallback, useRef } from 'react';
import {
  FormControlLabel, Chip, Box, Button, Checkbox, 
  Typography, Grid,
  Card, CardContent, CardHeader, IconButton
} from '@mui/material';
import BrowseIcon from '@mui/icons-material/Folder';
import AddIcon from '@mui/icons-material/Add';
import { useTranslation } from 'react-i18next';
import TagsDropdown from '../TagsDropdown';
import { useFolderPicker } from '../useFolderPicker';
import { useFolderRestrictions } from '../useFolderRestrictions';
import ActionSettingsLayout from '../ActionSettingsHeader';
import { useActionSettings } from '../../hooks/useActionSettings';
import TagEditor from '../Widgets/TagEditor';

const SettingCreateFile = ({ caseid, tags, settingId, onSaveStatusChange, initialScope = 'event' }) => {
  const { t } = useTranslation();
  const [target, setTarget] = useState('');
  const [useCustomContent, setUseCustomContent] = useState(false);
  const [useGenContent, setUseGenContent] = useState(false);
  const [customContent, setCustomContent] = useState('');
  const [genContent, setGenContent] = useState('');
  const [anchorEl, setAnchorEl] = useState(null); 
  const [menuType, setMenuType] = useState(''); 
  const [browsingDisabled, setBrowsingDisabled] = useState(false);

  const targetEditorRef = useRef(null);
  const contentEditorRef = useRef(null);
  const genEditorRef = useRef(null);

  // Unified action settings hook
  const {
    isEnabled,
    setIsEnabled,
    conditions,
    isSaveDisabled,
    forceRunLoading,
    forceRunResult,
    popUpOpen,
    forceRunWarningOpen,
    forceRunWarningCount,
    handleSave,
    handleForceRun,
    handleForceRunConfirm,
    handleForceRunCancel,
    handleConditionsChange,
    handleCloseStatusWindow,
    ws
  } = useActionSettings({
    settingId,
    caseid,
    actionType: 'createfile',
    onSaveStatusChange,
    initialScope,
    customComparison: (initial) => {
      return target === (initial.target || '') &&
             useCustomContent === (initial.useCustomContent || false) &&
             useGenContent === (initial.useGenContent || false) &&
             customContent === (initial.customContent || '') &&
             genContent === (initial.genContent || '');
    },
    customSaveData: () => ({
      target,
      useCustomContent,
      useGenContent,
      customContent,
      genContent
    }),
    loadDataFunction: (data) => {
      if (data.customData?.target !== undefined) setTarget(data.customData.target);
      if (data.customData?.useCustomContent !== undefined) setUseCustomContent(data.customData.useCustomContent);
      if (data.customData?.useGenContent !== undefined) setUseGenContent(data.customData.useGenContent);
      if (data.customData?.customContent !== undefined) setCustomContent(data.customData.customContent);
      if (data.customData?.genContent !== undefined) setGenContent(data.customData.genContent);
    }
  });

  const { restrictToUserFolder, allowedRoots, displayRoot } = useFolderRestrictions(caseid);

  const { handleBrowseDirectory, FolderBrowserComponent } = useFolderPicker({
    restrictToUserFolder,
    allowedRoots,
    displayRoot,
    ws,
    onFolderSelected: (folderPath) => {
      if (folderPath) {
        let folderPathClean = folderPath.replace(/\/+$/, "");
        const separator = folderPathClean.includes('\\') ? '\\' : '/';
        folderPathClean = folderPathClean + separator;
        setTarget(folderPathClean + "{filename}{file_extension}");
      }
      setBrowsingDisabled(false);
    },
    onFolderBrowserClose: () => {
      setBrowsingDisabled(false);
    }
  });


  const customDataTags = [{ key: 'tag_fileContentToWrite', label: 'Data Target File Content (if exist)', value: 'fileContentToWrite', description: 'Content of the target file if it already exists' }];

  const handleMenuOpen = (event, type) => {
    setAnchorEl(event.currentTarget);
    setMenuType(type);
  };

  const handleMainTagMenuClose = () => {
    setAnchorEl(null);
  };

  const handleTagSelect = (value) => {
    if (menuType === 'main' && targetEditorRef.current) {
      targetEditorRef.current.insertTag(value);
    } else if (menuType === 'content' && contentEditorRef.current) {
      contentEditorRef.current.insertTag(value);
    } else if (menuType === 'gen' && genEditorRef.current) {
      genEditorRef.current.insertTag(value);
    }
    setAnchorEl(null); 
  };

  return (
    <ActionSettingsLayout
      title="Create Files"
      isEnabled={isEnabled}
      onEnabledChange={setIsEnabled}
      isSaveDisabled={isSaveDisabled}
      onSave={handleSave}
      onForceRun={handleForceRun}
      forceRunLoading={forceRunLoading}
      forceRunResult={forceRunResult}
      popUpOpen={popUpOpen}
      onCloseStatusWindow={handleCloseStatusWindow}
      forceRunWarningOpen={forceRunWarningOpen}
      forceRunWarningCount={forceRunWarningCount}
      onForceRunConfirm={handleForceRunConfirm}
      onForceRunCancel={handleForceRunCancel}
      conditions={conditions}
      onConditionsChange={handleConditionsChange}
      tags={tags}
      customDataTags={customDataTags}
    >
      <Grid item xs={12}>
      {/* Where to save */}
      <Card sx={{
        mb: 2,
        borderRadius: '12px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
        border: '1px solid #5757b010',
        overflow: 'hidden',
      }}>
        <CardHeader
          title={
            <Typography sx={{
              fontWeight: 700,
              color: '#444',
              fontSize: { xs: '1rem', sm: '1.1rem' },
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 1
            }}>
              <Box component="span" sx={{
                bgcolor: '#5757b0',
                color: 'white',
                borderRadius: '6px',
                px: 1.5,
                py: 0.7,
                mr: { xs: 1, sm: 2 },
                fontSize: '0.85rem',
                letterSpacing: '0.5px',
                fontWeight: 600,
                boxShadow: '0 3px 6px rgba(87, 87, 176, 0.2)',
              }}>
                {t('auto_create_file.where_label')}
              </Box>
              {t('auto_create_file.where_to_save')}
            </Typography>
          }
          sx={{
            borderBottom: '1px solid #f0f0f8',
            backgroundColor: '#fafbff',
            borderTopLeftRadius: '12px',
            borderTopRightRadius: '12px',
            p: { xs: 1.5, sm: 2 },
          }}
        />
        <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
          <Box sx={{
            mb: 2,
            border: '1px solid #d0d0e8',
            borderRadius: '12px',
            overflow: 'hidden',
            boxShadow: '0 4px 12px rgba(87, 87, 176, 0.05)',
            backgroundColor: '#fff',
            transition: 'all 0.2s ease',
            '&:hover': {
              borderColor: '#5757b0',
              boxShadow: '0 8px 20px rgba(87, 87, 176, 0.1)',
            },
          }}>
            <Box sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: 'stretch',
            }}>
              <Box sx={{
                flex: 1,
                padding: '0px',
                minHeight: '28px', 
                fontFamily: 'SF Mono, Consolas, Monaco, monospace',
                overflow: 'hidden', 
                borderBottom: { xs: '1px solid #eaeaf0', sm: 'none' },
              }}>
                <TagEditor
                  ref={targetEditorRef}
                  value={target}
                  onChange={setTarget}
                  placeholder="File creation path (e.g. C:\\Folder\\{filename}{file_extension})"
                  minHeight="28px"
                  singleLine
                  onTagClick={(e) => handleMenuOpen(e, 'main')}
                  tags={tags}
                />
              </Box>
              <Button
                variant="contained"
                onClick={handleBrowseDirectory}
                disabled={browsingDisabled}
                sx={{
                  height: { xs: '40px', sm: 'auto' }, 
                  width: { xs: '100%', sm: 'auto' },
                  borderRadius: { xs: '0', sm: '0 12px 0 0' }, 
                  backgroundColor: '#5757b0',
                  color: 'white',
                  m: 0, 
                  px: 2.5,
                  '&:hover': {
                    backgroundColor: '#4a4a90',
                  },
                  '&.Mui-disabled': {
                    backgroundColor: '#c0c0d0',
                    color: 'white',
                  },
                  borderLeft: { sm: '1px solid #5757b060' },
                  transition: 'all 0.2s ease',
                }}
              >
                <BrowseIcon />
              </Button>
            </Box>

            <Box sx={{
              p: 2,
              display: 'flex',
              flexWrap: 'wrap',
              gap: 1,
              backgroundColor: '#f8f9ff',
              borderTop: '1px solid #eaeaf0',
            }}>
              <Typography sx={{
                fontWeight: 600,
                color: '#5757b0',
                fontSize: '0.85rem',
                mr: 1,
                display: 'flex',
                alignItems: 'center',
              }}>
                Quick Tags:
              </Typography>
              {[
                { label: 'Filename', value: 'filename' },
                { label: 'Extension', value: 'file_extension' },
                { label: 'Folder Path', value: 'file_folder_path' },
                { label: 'Year', value: 'year' },
                { label: 'Month', value: 'month' },
                { label: 'Day', value: 'day' },
              ].map(tag => (
                <Chip
                  key={tag.value}
                  label={tag.label}
                  onClick={() => {
                    if (targetEditorRef.current) {
                      targetEditorRef.current.focus();
                      setTimeout(() => {
                        targetEditorRef.current.insertTag(tag.value);
                      }, 10);
                    }
                  }}
                  size="small"
                  sx={{
                    backgroundColor: '#f0f0ff',
                    color: '#5757b0',
                    borderColor: '#5757b030',
                    border: '1px solid',
                    '&:hover': {
                      backgroundColor: '#e0e0ff',
                      boxShadow: '0 2px 6px rgba(87, 87, 176, 0.15)',
                    },
                    fontWeight: 600,
                    transition: 'all 0.2s ease',
                  }}
                />
              ))}
            </Box>
          </Box>
        </CardContent>
      </Card>
      </Grid>

      <Grid item xs={12}>
      {/* File options */}
      <Card sx={{
        mb: 0, 
        borderRadius: '12px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
        border: '1px solid #5757b010',
        overflow: 'hidden',
      }}>
        <CardHeader
          title={
            <Typography sx={{
              fontWeight: 700,
              color: '#444',
              fontSize: { xs: '1rem', sm: '1.1rem' },
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 1
            }}>
              <Box component="span" sx={{
                bgcolor: '#5757b0',
                color: 'white',
                borderRadius: '6px',
                px: 1.5,
                py: 0.7,
                mr: { xs: 1, sm: 2 },
                fontSize: '0.85rem',
                letterSpacing: '0.5px',
                fontWeight: 600,
                boxShadow: '0 3px 6px rgba(87, 87, 176, 0.2)',
              }}>
                {t('auto_create_file.what_label')}
              </Box>
              {t('auto_create_file.what_to_do')}
            </Typography>
          }
          sx={{
            borderBottom: '1px solid #f0f0f8',
            backgroundColor: '#fafbff',
            borderTopLeftRadius: '12px',
            borderTopRightRadius: '12px',
            p: { xs: 1.5, sm: 2 },
          }}
        />
        <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
          {/* Content options */}
          <Box sx={{
            mb: 2,
            backgroundColor: '#f8f9ff',
            borderRadius: '10px',
            padding: 2,
            border: '1px solid #eaeaf0',
            display: 'flex',
            flexDirection: 'column',
          }}>
            <Typography sx={{
              fontWeight: 600,
              color: '#5757b0',
              fontSize: '0.95rem',
              mb: 1,
            }}>
              Content Options:
            </Typography>
            
            <FormControlLabel
              control={
                <Checkbox
                  checked={useCustomContent}
                  onChange={(e) => {
                    const isChecked = e.target.checked;
                    setUseCustomContent(isChecked);
                    if (isChecked) {
                      setUseGenContent(false);
                    }
                  }}
                />
              }
              label={
                <Typography sx={{
                  fontWeight: 500,
                  fontSize: '0.9rem',
                  color: '#444',
                }}>
                  Use Custom Content
                </Typography>
              }
              sx={{ mb: 0.5 }}
            />
            
            <FormControlLabel
              control={
                <Checkbox
                  checked={useGenContent}
                  onChange={(e) => {
                    const isChecked = e.target.checked;
                    setUseGenContent(isChecked);
                    if (isChecked) {
                      setUseCustomContent(false);
                    }
                  }}
                />
              }
              label={
                <Typography sx={{
                  fontWeight: 500,
                  fontSize: '0.9rem',
                  color: '#444',
                }}>
                  Use Generative Content
                </Typography>
              }
            />
          </Box>

          {useCustomContent && (
            <Box sx={{ ml: 0, mt: 1.5 }}>
              <Box sx={{
                border: '1px solid #d0d0e8',
                padding: '0px',
                borderRadius: '8px',
                minHeight: '100px',
                mb: 1.5,
                backgroundColor: '#fff',
                '&:hover': {
                  borderColor: '#5757b0',
                  boxShadow: '0 4px 12px rgba(87, 87, 176, 0.08)',
                },
                transition: 'all 0.2s ease',
                boxShadow: '0 2px 8px rgba(0,0,0,0.02)',
                fontFamily: 'SF Mono, Consolas, Monaco, monospace',
              }}>
                <TagEditor
                  ref={contentEditorRef}
                  value={customContent}
                  onChange={setCustomContent}
                  placeholder="Type custom content here..."
                  onTagClick={(e) => handleMenuOpen(e, 'content')}
                  tags={tags}
                />
              </Box>
            </Box>
          )}

          {useGenContent && (
            <Box sx={{ ml: 0, mt: 1.5 }}>
              <Box sx={{
                border: '1px solid #d0d0e8',
                padding: '0px',
                borderRadius: '8px',
                minHeight: '100px',
                mb: 1.5,
                backgroundColor: '#fff',
                '&:hover': {
                  borderColor: '#5757b0',
                  boxShadow: '0 4px 12px rgba(87, 87, 176, 0.08)',
                },
                transition: 'all 0.2s ease',
                boxShadow: '0 2px 8px rgba(0,0,0,0.02)',
                fontFamily: 'SF Mono, Consolas, Monaco, monospace',
              }}>
                <TagEditor
                  ref={genEditorRef}
                  value={genContent}
                  onChange={setGenContent}
                  placeholder="Type generation instructions here..."
                  tags={tags}
                  onTagClick={(e) => handleMenuOpen(e, 'gen')}
                />
              </Box>
            </Box>
          )}
        </CardContent>
      </Card>
      </Grid>
      
      {/* Tag dropdown */}
      <TagsDropdown
        tags={tags}
        handleTagSelect={handleTagSelect}
        anchorEl={anchorEl}
        setAnchorEl={handleMainTagMenuClose} 
        menuType={menuType} 
        additionalTags={customDataTags}
      />

      {FolderBrowserComponent}
    </ActionSettingsLayout>
  );
};

export default React.memo(SettingCreateFile);
